import React, { useMemo, useState } from 'react';
import { Selection, } from '@fluentui/react';
import { getAllChildrenOfNode, getRootOfNode, getSiblingsOfNode, isLeaf, WithTreeViewData } from './treeViewHelper';
import { TreeViewSelectionZone } from './TreeViewSelectionZone';

interface Item {
  Id: number;
  [custom: string]: any;
}

type UseTreeViewSelectionProps = {
  isTreeView: false;
  items: Item[];
} | {
  isTreeView: true;
  items: WithTreeViewData<Item>[];
}

function getKey(item: Item) { return item.Id }

export function useTreeViewSelection(props: UseTreeViewSelectionProps) {
  const { isTreeView, items } = props;
  const [selectedRowIds, setSelectedRowIds] = useState(new Set<number>());

  const selection = useMemo(() => new Selection({
    getKey,
  }), [isTreeView]);

  // update selections
  if (isTreeView) {
    const isSelectionZoneAllSelected = selection.isAllSelected();
    // TODO: remove this `as any` after upgrade TS to a new version.
    const rootId = items.length !== 0 ? getKey(getRootOfNode(items[0] as any)) : undefined;
    const isAllReallySelected = rootId ? selectedRowIds.has(rootId) : false;
    // if the state not match, need to trigger the event to update `SelectAll Checkbox` in the grid
    const isEventTriggered = isSelectionZoneAllSelected !== isAllReallySelected;
    selection.setChangeEvents(false);
    selection.setItems(items);
    selectedRowIds.forEach((id) => {
      selection.setKeySelected(id.toString(), true, false);
    })
    selection.setChangeEvents(true, !isEventTriggered);
  }

  const finalSelection: any = isTreeView ? selection : undefined;

  return {
    selection: finalSelection,
    disableSelectionZone: isTreeView,
    listProps: !isTreeView ? undefined : {
      // eslint-disable-next-line @typescript-eslint/no-shadow
      onRenderRoot: (props) => {
        const { rootRef, surfaceElement, divProps } = props;
        return (
          <TreeViewSelectionZone
            selection={finalSelection}
            selectionClearedOnSurfaceClick={false}
            selectionPreservedOnEmptyClick
            // eslint-disable-next-line @typescript-eslint/no-shadow, @typescript-eslint/no-unused-vars
            onToggleSelected={(selection, index, ev) => {
              const item = items[index];
              const selectionKeyOfItem = getKey(item);
              // Trigger click event on the checkbox to imitate `Group` in DetailsList.
              // We could not use `Group` directly, for the group row is unable to show data.
              // Behaviors:
              // 1. If parent is clicked, from unchecked to checked -- all children should be checked
              // 2. If parent is clicked, from checked to unchecked -- all children should be unchecked
              // 3. If child is clicked, from checked to unchecked, if parent is checked, set parent to uncheck, this should be recursive
              // 4. If child is clicked, from unchecked to checked, if all other children are checked, set parent to check, this should be recursive
              // one node mighe be child/parent at the same time.
              setSelectedRowIds((pre) => {
                // toggle clicked item itself
                if (pre.has(selectionKeyOfItem)) {
                  pre.delete(selectionKeyOfItem);
                } else {
                  pre.add(selectionKeyOfItem);
                }

                if (!isLeaf(item as any)) {
                  const children = getAllChildrenOfNode(item as any);
                  // situation 1
                  if (pre.has(selectionKeyOfItem)) {
                    children.forEach((child) => {
                      pre.add(child.Id);
                    });
                  // eslint-disable-next-line brace-style
                  }
                  // situation 2
                  else {
                    children.forEach((child) => {
                      pre.delete(child.Id);
                    });
                  }
                }

                let currentItem = item;
                while (true) {
                  const siblings = getSiblingsOfNode(currentItem as any);
                  if (!siblings) {
                    break;
                  }
                  // situation 3
                  if (!pre.has(selectionKeyOfItem)) {
                    if (currentItem.treeViewInfo.parent) {
                      pre.delete(currentItem.treeViewInfo.parent.Id);
                      currentItem = currentItem.treeViewInfo.parent;
                    } else {
                      break;
                    }
                  // eslint-disable-next-line brace-style
                  }
                  // situation 4
                  else if (currentItem.treeViewInfo.parent
                      && siblings.every(sibling => pre.has(sibling.Id))
                    ) {
                      pre.add(currentItem.treeViewInfo.parent.Id);
                      currentItem = currentItem.treeViewInfo.parent;
                    } else {
                      break;
                    }
                }
                return new Set(pre);
              })
            }
            }
          >
            <div ref={rootRef} {...divProps}>
              {surfaceElement}
            </div>
          </TreeViewSelectionZone>
        );
      },
    },
  }
}
